import type {
  CaseStatus,
  CaseTier,
  CaseType,
  CaseChannel,
  ResolutionPathway,
  HRCase,
  QueueMetrics,
} from '../types/case.types'

// ── Usecase taxonomy ──────────────────────────────────────────────────────────
// Maps each CaseType to a human-readable usecase label, used for queue filtering.

export const USECASE_MAP: Record<CaseType, string> = {
  // L1 usecases
  pto_leave_balance:           'Leave & PTO',
  benefits_policy_question:    'Benefits & Policy',
  payroll_query:               'Payroll Queries',
  employment_verification:     'Employment Verification',
  address_data_change:         'Data Changes',
  general_hr_faq:              'General HR FAQ',
  // L2 usecases
  entitlement_eligibility:     'Entitlement & Eligibility',
  multi_system_transaction:    'Multi-System Transactions',
  exception_appeal:            'Exception Appeals',
  sensitive_employee_relations:'Employee Relations',
  accommodation_disability:    'ADA Accommodations',
  performance_data_request:    'Performance Data',
}

// Which usecases belong to each tier
export const L1_USECASES = [
  'Leave & PTO',
  'Benefits & Policy',
  'Payroll Queries',
  'Employment Verification',
  'Data Changes',
  'General HR FAQ',
] as const

export const L2_USECASES = [
  'Entitlement & Eligibility',
  'Multi-System Transactions',
  'Exception Appeals',
  'Employee Relations',
  'ADA Accommodations',
  'Performance Data',
] as const

export type UsecaseLabel = (typeof L1_USECASES)[number] | (typeof L2_USECASES)[number]

// ── SLA ────────────────────────────────────────────────────────────────────

export function getSlaRemainingMs(slaDeadline: string): number {
  return new Date(slaDeadline).getTime() - Date.now()
}

export function formatSlaRemaining(slaDeadline: string): string {
  const ms = getSlaRemainingMs(slaDeadline)
  if (ms < 0) return 'Due'
  const h = Math.floor(ms / 3_600_000)
  const m = Math.floor((ms % 3_600_000) / 60_000)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

export function isSlaUrgent(slaDeadline: string): boolean {
  return getSlaRemainingMs(slaDeadline) < 2 * 3_600_000 // < 2 hours
}

// ── Status ─────────────────────────────────────────────────────────────────

export const STATUS_LABELS: Record<CaseStatus, string> = {
  new: 'New',
  investigating: 'Investigating',
  awaiting_approval: 'Awaiting Approval',
  in_workbench: 'In Workbench',
  resolved: 'Resolved',
  escalated: 'Escalated',
}

export const STATUS_COLORS: Record<CaseStatus, string> = {
  new: 'bg-sky-100 text-sky-700 border-sky-200',
  investigating: 'bg-amber-100 text-amber-700 border-amber-200',
  awaiting_approval: 'bg-purple-100 text-purple-700 border-purple-200',
  in_workbench: 'bg-orange-100 text-orange-700 border-orange-200',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  escalated: 'bg-red-100 text-red-700 border-red-200',
}

export const STATUS_DOT: Record<CaseStatus, string> = {
  new: 'bg-sky-500',
  investigating: 'bg-amber-500',
  awaiting_approval: 'bg-purple-500',
  in_workbench: 'bg-orange-500',
  resolved: 'bg-emerald-500',
  escalated: 'bg-red-500',
}

// ── Tier ───────────────────────────────────────────────────────────────────

export const TIER_COLORS: Record<CaseTier, string> = {
  L1: 'bg-brand-cornflower/10 text-brand-cornflower border-brand-cornflower/30',
  L2: 'bg-brand-navy/10 text-brand-navy border-brand-navy/30',
}

export function tierLabel(tier: CaseTier): string {
  return tier === 'L1' ? 'L1 Standard' : 'L2 Complex'
}

// ── Case Type ──────────────────────────────────────────────────────────────

export const CASE_TYPE_LABELS: Record<CaseType, string> = {
  pto_leave_balance: 'PTO / Leave Balance',
  benefits_policy_question: 'Benefits Policy',
  payroll_query: 'Payroll Query',
  employment_verification: 'Employment Verification',
  address_data_change: 'Address / Data Change',
  entitlement_eligibility: 'Entitlement Eligibility',
  multi_system_transaction: 'Multi-System',
  exception_appeal: 'Exception Appeal',
  sensitive_employee_relations: 'Employee Relations',
  accommodation_disability: 'Accommodation',
  general_hr_faq: 'General HR FAQ',
  performance_data_request: 'Performance Data',
}

// ── Case Type Badge Colors (grouped by category) ──────────────────────────

export const CASE_TYPE_BADGE: Record<CaseType, string> = {
  // Leave / Benefits group → sky-blue
  pto_leave_balance: 'bg-sky-100 text-sky-700 border-sky-200',
  benefits_policy_question: 'bg-sky-100 text-sky-700 border-sky-200',
  entitlement_eligibility: 'bg-sky-100 text-sky-700 border-sky-200',
  // Payroll / Multi-system → purple
  payroll_query: 'bg-purple-100 text-purple-700 border-purple-200',
  multi_system_transaction: 'bg-purple-100 text-purple-700 border-purple-200',
  // Employment / Data → teal
  employment_verification: 'bg-teal-100 text-teal-700 border-teal-200',
  address_data_change: 'bg-teal-100 text-teal-700 border-teal-200',
  // Sensitive / Exception → red/orange
  sensitive_employee_relations: 'bg-red-100 text-red-700 border-red-200',
  accommodation_disability: 'bg-orange-100 text-orange-700 border-orange-200',
  exception_appeal: 'bg-amber-100 text-amber-700 border-amber-200',
  // General / Performance → green / indigo
  general_hr_faq: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  performance_data_request: 'bg-indigo-100 text-indigo-700 border-indigo-200',
}

// ── Channel ────────────────────────────────────────────────────────────────

export const CHANNEL_LABELS: Record<CaseChannel, string> = {
  teams: 'Microsoft Teams',
  email: 'Email',
  portal: 'HR Portal',
  hris_triggered: 'HRIS Triggered',
  workday: 'Workday',
}

export const CHANNEL_ICONS: Record<CaseChannel, string> = {
  teams: 'Teams',
  email: 'Email',
  portal: 'Portal',
  hris_triggered: 'HRIS',
  workday: 'Workday',
}

// ── Resolution Pathway ─────────────────────────────────────────────────────

export const PATHWAY_LABELS: Record<ResolutionPathway, string> = {
  autonomous: 'Autonomous',
  approval_required: 'Approval Required',
  workbench_escalation: 'Workbench Escalation',
}

export const PATHWAY_COLORS: Record<ResolutionPathway, string> = {
  autonomous: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  approval_required: 'bg-purple-100 text-purple-700 border-purple-200',
  workbench_escalation: 'bg-orange-100 text-orange-700 border-orange-200',
}

// ── Time formatting ────────────────────────────────────────────────────────

export function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export function formatDuration(startIso: string, endIso?: string): string {
  const ms = (endIso ? new Date(endIso) : new Date()).getTime() - new Date(startIso).getTime()
  const m = Math.floor(ms / 60_000)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`
}

// ── Filtering / sorting helpers ────────────────────────────────────────────

export function filterCases(
  cases: HRCase[],
  opts: {
    status?: CaseStatus | 'all'
    tier?: CaseTier | 'all'
    usecase?: string | 'all'
    search?: string
  }
): HRCase[] {
  return cases.filter((c) => {
    if (opts.status && opts.status !== 'all' && c.status !== opts.status) return false
    if (opts.tier && opts.tier !== 'all' && c.tier !== opts.tier) return false
    if (opts.usecase && opts.usecase !== 'all') {
      const caseUsecase = USECASE_MAP[c.type as CaseType]
      if (caseUsecase !== opts.usecase) return false
    }
    if (opts.search) {
      const q = opts.search.toLowerCase()
      if (
        !c.caseRef.toLowerCase().includes(q) &&
        !c.employee.name.toLowerCase().includes(q) &&
        !c.subject.toLowerCase().includes(q)
      )
        return false
    }
    return true
  })
}

export function sortByNewest(cases: HRCase[]): HRCase[] {
  return [...cases].sort(
    (a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
  )
}

// ── Employee sub-line ──────────────────────────────────────────────────────

/**
 * The "Name · Department" line the compact case cards carry, plus anything else
 * the caller wants on the end of it.
 *
 * Empty parts are dropped rather than printed, so a case whose record holds no
 * department reads as a name and not as a name followed by a dangling separator.
 * A bridged AI Employee run is exactly that case: the run endpoint carries no
 * directorate, and a made-up one would be a made-up fact about a real person.
 */
export function employeeLine(c: HRCase, ...extra: string[]): string {
  return [c.employee.name, c.employee.department, ...extra].filter(Boolean).join(' · ')
}

// ── Queue grouping: ONE classifier, and only one ───────────────────────────
//
// Four places used to answer "how many cases are open" their own way, on one
// screen:
//
//   the Live Queue chips  new, investigating, awaiting approval
//   the dashboard KPI band  those three plus the workbench and the escalations,
//                           counted over the mock array at module load and so
//                           blind to anything the store gained afterwards
//   the Live Queue tab badge  everything that is not resolved or escalated
//   the AI snapshot  everything that is not resolved or escalated, over mocks
//
// Three different numbers for one question. Everything in this module now counts
// through the classifier below, so the KPI card and the chip beneath it cannot
// say different things about the same store.

export type CaseGroup = 'open' | 'workbench' | 'resolved'

/** Which of the queue's three groups a case belongs to. Every case belongs to exactly one. */
export function caseGroup(c: HRCase): CaseGroup {
  if (c.status === 'in_workbench') return 'workbench'
  if (c.status === 'resolved' || c.status === 'escalated') return 'resolved'
  return 'open'
}

/** Open: with the AI Employee or with a person, and not yet closed out. */
export function isOpenCase(c: HRCase): boolean {
  return caseGroup(c) === 'open'
}

/** In the Auto Workbench, waiting on a specialist. */
export function isWorkbenchCase(c: HRCase): boolean {
  return caseGroup(c) === 'workbench'
}

/** Closed out, whether resolved by the AI Employee or escalated away. */
export function isResolvedCase(c: HRCase): boolean {
  return caseGroup(c) === 'resolved'
}

/**
 * The KPI band, counted from the cases the store is actually holding.
 *
 * `avgResolutionMinutes` is passed in rather than derived: no case carries a
 * per-case duration, so it is an authored narrative figure and is kept beside the
 * mock data that owns it. Everything else here is a count of real rows.
 *
 * `autonomousResolutionRate` reads `resolutionPathway`, the recorded field that
 * matches the claim the card makes, rather than standing in the L1 share of the
 * resolved pile. A tier is not a pathway: an L2 case resolved without a human is
 * autonomous and an L1 case that needed an approval is not, and once real runs
 * carrying real approvals joined the store the proxy and the claim parted company.
 */
export function computeQueueMetrics(
  cases: HRCase[],
  authored: { avgResolutionMinutes: number }
): QueueMetrics {
  const open = cases.filter(isOpenCase)
  const resolved = cases.filter(isResolvedCase)
  const autonomous = resolved.filter((c) => c.resolutionPathway === 'autonomous').length

  return {
    totalOpen: open.length,
    l1Open: open.filter((c) => c.tier === 'L1').length,
    l2Open: open.filter((c) => c.tier === 'L2').length,
    inWorkbench: cases.filter(isWorkbenchCase).length,
    resolved: resolved.length,
    autonomousResolutionRate: resolved.length
      ? Math.round((autonomous / resolved.length) * 100)
      : 0,
    avgResolutionMinutes: authored.avgResolutionMinutes,
    slaBreach: cases.filter((c) => c.slaBreached).length,
  }
}
